// The diner's place in a restaurant's menu (UC-31, #1020). Switching rows and
// cards, or turning a tablet across 640 px, changes every item's height, so
// the item that sat under the pinned bar would slide away. Before the change
// the place notes the first item shown whose bottom is below the bar's lower
// edge, and how far its top sits from that edge; after the change it scrolls
// that item back to the same offset. At the page top, before the menu has
// scrolled under the bar, or when the filter shows no item, there is no
// place and nothing scrolls. It never relies on the browser's own scroll
// anchoring, which not every browser has.

/** What the place reads of an element: its box on the screen. */
export interface Box {
	getBoundingClientRect(): { top: number; bottom: number };
}

/** The parts of the page the place reads, and how it scrolls. */
export interface Page {
	/** The pinned bar: the menu filter. */
	bar: Box;
	/** The menu, which scrolls under the bar. */
	menu: Box;
	/** The items shown, in the menu's order; the filter's hidden ones left out. */
	items: () => Iterable<Box>;
	/** Whether an item still shows: the filter may have hidden it since. */
	shows: (item: Box) => boolean;
	scrollBy: (top: number) => void;
}

/** The item under the bar's lower edge, and its top's distance from it. */
export interface Place {
	item: Box;
	offset: number;
}

export function note(page: Page): Place | null {
	const edge = page.bar.getBoundingClientRect().bottom;
	if (page.menu.getBoundingClientRect().top >= edge) return null;
	for (const item of page.items()) {
		const box = item.getBoundingClientRect();
		if (box.bottom > edge) return { item, offset: box.top - edge };
	}
	return null;
}

export function restore(page: Page, place: Place | null): void {
	if (!place || !page.shows(place.item)) return;
	const edge = page.bar.getBoundingClientRect().bottom;
	const moved = place.item.getBoundingClientRect().top - edge - place.offset;
	if (moved !== 0) page.scrollBy(moved);
}

/** Makes a change, keeping the item under the bar where it was. */
export function keep(page: Page | null, change: () => void): void {
	const place = page ? note(page) : null;
	change();
	if (page) restore(page, place);
}

/** Whether an element draws a box: one the filter hid, or a hidden copy, draws none. */
export const drawn = (el: Element): boolean => el.getClientRects().length > 0;

/** The page's own parts, by the marks the worker drew; null off a menu. */
export function onPage(): Page | null {
	const bar = document.querySelector<HTMLElement>('[data-island="filter"]');
	const menu = document.querySelector<HTMLElement>('[data-menu]');
	if (!bar || !menu) return null;
	return {
		bar,
		menu,
		// Read one at a time, so a note stops at the first item under the bar.
		*items() {
			for (const item of menu.querySelectorAll<HTMLElement>('[data-menu-item]')) {
				if (drawn(item)) yield item;
			}
		},
		shows: (item) => drawn(item as HTMLElement),
		scrollBy: (top) => window.scrollBy({ top, behavior: 'instant' }),
	};
}
